'use client';

import { useEffect } from 'react';

import Script from 'next/script';

import {
	subscribeAnalyticsConsent,
	type AnalyticsConsent,
} from '../consent/analytics-consent';
import { isIndexableDeployment } from '@naumu/shared/seo/indexability';

import { buildConsentDefaultsByDecision, buildConsentUpdate } from './consent-mode';
import { gtagCommand } from './data-layer';

/**
 * Google Tag Manager container. Feeds Google Ads conversion tracking (the
 * container itself is configured by marketing in the GTM UI, not here — see
 * ./CONSENT.md for the checklist).
 *
 * Loaded on the production-indexable deployment only, reusing
 * `isIndexableDeployment()` — the same single source of truth that keeps
 * staging out of the search index keeps staging/dev traffic out of ad reports.
 *
 * Consent: Google Consent Mode v2. The container loads immediately with
 * ad/analytics storage DENIED in the EEA + UK (cookieless pings only) and
 * GRANTED elsewhere, and flips on the existing analytics-consent banner — the
 * same single decision that opts PostHog in. PostHog is NOT routed through
 * GTM; it is proxied via `/ingest/*`.
 */

/**
 * Container id. `NEXT_PUBLIC_GTM_ID` is the knob; the literal is the container
 * that was hardcoded here before and stays as the fallback so production
 * tracking does not go dark the moment this file changes. Set the env var to
 * point at a different container.
 */
const DEFAULT_GTM_CONTAINER_ID = 'GTM-MDSMRTN9';

export const getGtmContainerId = (): string | null => {
	const configured = process.env.NEXT_PUBLIC_GTM_ID?.trim();
	if (configured) return configured;
	return DEFAULT_GTM_CONTAINER_ID;
};

/**
 * The container loads only when an id exists, the deployment is the indexable
 * production one, and the kill switch is off. Any of the three failing means
 * no gtm.js request at all — not a loaded-but-idle container.
 */
export const isGtmEnabled = (): boolean =>
	getGtmContainerId() !== null &&
	isIndexableDeployment() &&
	process.env.NEXT_PUBLIC_GTM_DISABLED !== 'true';

/** Mirrors STORAGE_KEY in lib/consent/analytics-consent (read inside a raw script). */
const CONSENT_STORAGE_KEY = 'naumu:analytics-consent';

/**
 * Inline bootstrap: declares the dataLayer, sets Consent Mode v2 defaults for
 * the visitor's persisted decision (falling back to the region split when they
 * have not answered), sets `url_passthrough` + `ads_data_redaction`, and only
 * then starts the container. One script so the ordering is unconditional —
 * Consent Mode ignores a default that arrives after gtm.js.
 *
 * `ads_data_redaction` strips ad identifiers from pings while ad_storage is
 * denied; `url_passthrough` carries gclid/gbraid through client-side
 * navigations so a denied-consent visitor is still attributable to a click.
 */
const buildBootstrap = (containerId: string) => {
	const defaults = JSON.stringify(buildConsentDefaultsByDecision());
	return `
(function(w,d,s,l,i){
  var p=w.location.pathname;if(p==='/join'||p.indexOf('/join/')===0||p==='/h'||p.indexOf('/h/')===0)return;/* mirrors analytics/credential-routes.ts (inline: this runs before any module) */
  w[l]=w[l]||[];
  function gtag(){w[l].push(arguments);}
  var D=${defaults};
  var k='undecided';
  try{var v=localStorage.getItem(${JSON.stringify(CONSENT_STORAGE_KEY)});if(v==='granted'||v==='denied'){k=v;}}catch(e){}
  var cmds=D[k];
  for(var n=0;n<cmds.length;n++){gtag('consent','default',cmds[n]);}
  gtag('set','url_passthrough',true);
  gtag('set','ads_data_redaction',true);
  w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});
  var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
  j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
  f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer',${JSON.stringify(containerId)});
`;
};

/**
 * Bridge the banner decision into Consent Mode.
 *
 * Fired from the SAME synchronous handler as `applyAnalyticsConsent` —
 * `setAnalyticsConsent` calls PostHog and dispatches the consent event in one
 * go — so Google and PostHog can never end up on opposite sides of a decision.
 */
export const pushConsentUpdate = (consent: AnalyticsConsent) => {
	gtagCommand('consent', 'update', buildConsentUpdate(consent));
};

export const GoogleTagManager = () => {
	const enabled = isGtmEnabled();
	const containerId = getGtmContainerId();

	useEffect(() => {
		if (!enabled) return;
		return subscribeAnalyticsConsent(pushConsentUpdate);
	}, [enabled]);

	if (!enabled || !containerId) return null;

	return (
		<>
			<Script
				id="gtm-bootstrap"
				strategy="afterInteractive"
				dangerouslySetInnerHTML={{ __html: buildBootstrap(containerId) }}
			/>
			<noscript>
				<iframe
					src={`https://www.googletagmanager.com/ns.html?id=${containerId}`}
					height="0"
					width="0"
					style={{ display: 'none', visibility: 'hidden' }}
					title="Google Tag Manager"
				/>
			</noscript>
		</>
	);
};
